import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import type { OrdersMatrixReport } from "@/api/orders";
import * as planningApi from "@/api/orders-planning";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { saveBlob } from "@/lib/download";
import { cn } from "@/lib/utils";
import { api } from "@/api/client";
import { ArticleDetail } from "./ArticleDetail";
import { BulkActionMenu } from "./BulkActionMenu";
import { Insights, MpDonut, OrdersBarChart } from "./Charts";
import { ComparisonPanel } from "./ComparisonPanel";
import { expandRules, type WarehouseAggRow } from "./clusters";
import { buildArticlesCsv, buildDetailsCsv, downloadTextFile, type ArticleCsvRow } from "./csv";
import { int0, monthFullLabel, signed } from "./format";
import { MatrixTable, type MatrixExtras, type SortState, type TableMode } from "./MatrixTable";
import {
  applyViewFilters,
  buildView,
  computeKpis,
  computeQuality,
  rowMetrics,
  type QuickFilter,
  type Tab,
} from "./metrics";
import { useOrdersEdit } from "./useOrdersEdit";

const QUICK_FILTERS: { value: QuickFilter; label: string }[] = [
  { value: "", label: "Все" },
  { value: "current", label: "Есть продажи в тек. мес" },
  { value: "no-current", label: "Без продаж в тек. мес" },
  { value: "stock", label: "Есть остаток" },
  { value: "risk", label: "Требует внимания" },
  { value: "drop", label: "Просадка" },
  { value: "growth", label: "Рост" },
];

const ABC_VALUES = ["AX", "AY", "AZ", "BX", "BY", "BZ", "CX", "CY", "CZ"];

const MODE_LABELS: Record<TableMode, string> = {
  history: "Месяцы: заказы",
  price: "Месяцы: цена",
  margin: "Месяцы: маржа",
  compact: "Компактно",
};

export function MatrixTab({
  report,
  tab,
}: {
  report: OrdersMatrixReport;
  tab: Tab;
}): React.ReactElement {
  const qc = useQueryClient();
  const canEdit = useOrdersEdit();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [abc, setAbc] = useState("");
  const [brand, setBrand] = useState("");
  const [group, setGroup] = useState("");
  const [characteristic, setCharacteristic] = useState("");
  const [quick, setQuick] = useState<QuickFilter>("");
  const [mode, setMode] = useState<TableMode>("history");
  const [sort, setSort] = useState<SortState | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [compared, setCompared] = useState<Set<string>>(new Set());
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 180);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ── справочники и v2-данные ──
  const attrsQ = useQuery({ queryKey: ["orders", "refs", "attrs"], queryFn: planningApi.listAttrs });
  const charsQ = useQuery({
    queryKey: ["orders", "refs", "characteristics"],
    queryFn: planningApi.listCharacteristics,
  });
  const typesQ = useQuery({
    queryKey: ["orders", "refs", "product-types"],
    queryFn: planningApi.listProductTypes,
  });
  const clustersQ = useQuery({
    queryKey: ["orders", "refs", "clusters"],
    queryFn: planningApi.listClusters,
  });
  const highlightsQ = useQuery({
    queryKey: ["orders", "refs", "highlights"],
    queryFn: planningApi.listHighlights,
  });
  const controlQ = useQuery({ queryKey: ["orders", "control"], queryFn: planningApi.listControl });
  const channelsQ = useQuery({
    queryKey: ["orders", "refs", "channels"],
    queryFn: planningApi.listChannels,
  });
  // план текущего месяца основного диапазона — для колонок «План мес»/«Вып.»
  const currentMonth = report.months[report.months.length - 1] ?? "";
  const planYear = Number(currentMonth.slice(0, 4)) || new Date(report.date_to).getFullYear();
  const salesPlanQ = useQuery({
    queryKey: ["orders", "sales-plan", [planYear]],
    queryFn: () => planningApi.listSalesPlan(planYear),
  });
  const whAggQ = useQuery({
    queryKey: ["orders", "wh-agg", report.date_from, report.date_to],
    queryFn: () =>
      planningApi.getWarehousesAgg({ date_from: report.date_from, date_to: report.date_to }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const attrsMap = useMemo(
    () => new Map((attrsQ.data ?? []).map((a) => [a.article, a])),
    [attrsQ.data],
  );
  const charColors = useMemo(
    () => new Map((charsQ.data ?? []).map((c) => [c.name, c.color])),
    [charsQ.data],
  );
  const highlights = useMemo(
    () => new Map((highlightsQ.data ?? []).map((h) => [h.article, h.color])),
    [highlightsQ.data],
  );
  const controlSet = useMemo(
    () => new Set((controlQ.data ?? []).map((c) => `${c.mp}::${c.article}`)),
    [controlQ.data],
  );
  const clusterData = useMemo(() => {
    if (!whAggQ.data || !clustersQ.data) return null;
    const byArticle = new Map<string, WarehouseAggRow[]>();
    for (const row of whAggQ.data.rows) {
      const list = byArticle.get(row.article);
      if (list) list.push(row);
      else byArticle.set(row.article, [row]);
    }
    return {
      byArticle,
      rules: expandRules(clustersQ.data),
      selectedDays: whAggQ.data.selected_days,
    };
  }, [whAggQ.data, clustersQ.data]);

  // порт currentPlanForRow: свод — сумма всех каналов, МП-вкладка — канал вкладки
  const planByArticle = useMemo(() => {
    if (!salesPlanQ.data || !currentMonth) return null;
    const monthNum = Number(currentMonth.slice(5, 7));
    const map = new Map<string, number>();
    for (const cell of salesPlanQ.data) {
      if (cell.month !== monthNum) continue;
      if (tab !== "all" && cell.channel_code !== tab) continue;
      map.set(cell.article, (map.get(cell.article) ?? 0) + cell.qty);
    }
    return map.size ? map : null;
  }, [salesPlanQ.data, currentMonth, tab]);
  void channelsQ;

  const { rows, ctx } = useMemo(() => buildView(report, tab, search), [report, tab, search]);

  const brands = useMemo(
    () => [...new Set(rows.map((r) => r.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ru")),
    [rows],
  );

  const filtered = useMemo(
    () => applyViewFilters(rows, ctx, { abc, brand, quick, group, characteristic, attrs: attrsMap }),
    [rows, ctx, abc, brand, quick, group, characteristic, attrsMap],
  );

  const kpis = useMemo(() => computeKpis(filtered, ctx, report, tab), [filtered, ctx, report, tab]);
  const quality = useMemo(
    () => computeQuality(filtered, ctx, report, tab),
    [filtered, ctx, report, tab],
  );
  const attrsQuality = useMemo(() => {
    let noType = 0;
    let noChar = 0;
    for (const row of filtered) {
      const a = attrsMap.get(row.article);
      if (!a?.product_type) noType += 1;
      if (!a?.characteristic) noChar += 1;
    }
    return { noType, noChar };
  }, [filtered, attrsMap]);

  // ── мутации ──
  const invalidateRefs = () => qc.invalidateQueries({ queryKey: ["orders", "refs"] });
  const commentMut = useMutation({
    mutationFn: (v: { article: string; comment: string }) =>
      planningApi.upsertAttrs({ article: v.article, comment: v.comment }),
    onSuccess: invalidateRefs,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить комментарий")),
  });
  const controlMut = useMutation({
    mutationFn: async (v: { row: { article: string; name: string; brand: string; code_1c: string; id_1c: string }; checked: boolean }) => {
      if (tab === "all") return;
      if (v.checked) {
        await planningApi.addControl({
          mp: tab,
          article: v.row.article,
          name: v.row.name,
          brand: v.row.brand,
          code_1c: v.row.code_1c,
          id_1c: v.row.id_1c,
        });
      } else {
        await planningApi.removeControl({ mp: tab, article: v.row.article, reason: "uncheck" });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", "control"] }),
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось обновить контроль")),
  });
  const highlightMut = useMutation({
    mutationFn: (v: { articles: string[]; color: string | null }) =>
      planningApi.bulkHighlights(v.articles, v.color),
    onSuccess: () => {
      invalidateRefs();
      setBulkSelected(new Set());
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось изменить подсветку")),
  });

  const resetFilters = (): void => {
    setSearchInput("");
    setSearch("");
    setAbc("");
    setBrand("");
    setGroup("");
    setCharacteristic("");
    setQuick("");
    setSort(null);
    setCompared(new Set());
  };

  const extras: MatrixExtras = {
    attrs: attrsMap,
    charColors,
    highlights,
    clusters: clusterData,
    control:
      tab !== "all"
        ? {
            set: controlSet,
            mp: tab,
            toggle: (row, checked) => controlMut.mutate({ row, checked }),
          }
        : null,
    compare: {
      set: compared,
      toggle: (key) =>
        setCompared((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        }),
    },
    bulk: {
      set: bulkSelected,
      toggle: (article) =>
        setBulkSelected((prev) => {
          const next = new Set(prev);
          if (next.has(article)) next.delete(article);
          else next.add(article);
          return next;
        }),
    },
    plan: planByArticle,
    canEdit,
    onComment: (article, comment) => commentMut.mutate({ article, comment }),
  };

  return (
    <div className="space-y-4">
      {/* KPI — порт renderKpis: всё в штуках */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-8">
        <Kpi label={monthFullLabel(kpis.currentMonth)} value={int0(kpis.current)} />
        <Kpi label={monthFullLabel(kpis.previousMonth)} value={int0(kpis.previous)} />
        <Kpi
          label="Δ к пред. месяцу"
          value={signed(kpis.delta)}
          valueClass={kpis.delta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}
        />
        <Kpi label="Продажи 30 дней" value={int0(kpis.qty30)} />
        <Kpi label="Всего за период" value={int0(kpis.totalQty)} />
        <Kpi label="Артикулов" value={int0(kpis.articles)} />
        <Kpi
          label="Активных (30 дн)"
          value={int0(kpis.active)}
          onClick={() => setQuick(quick === "active" ? "" : "active")}
          active={quick === "active"}
        />
        <Kpi label="Без артикула 1С, шт" value={int0(kpis.unmappedQty)} />
      </div>

      {/* Карточки качества — кликабельные фильтры */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
        <Kpi small label="МП с непривязанными SKU" value={int0(quality.unmappedMps)} />
        <Kpi small label="Без названия" value={int0(quality.noName)} />
        <Kpi
          small
          label="Остаток без продаж 30д"
          value={int0(quality.stockNoSales)}
          onClick={() => setQuick(quick === "stock-no-sales" ? "" : "stock-no-sales")}
          active={quick === "stock-no-sales"}
        />
        <Kpi
          small
          label="Продажи без остатка МП"
          value={int0(quality.salesNoStock)}
          onClick={() => setQuick(quick === "sales-no-stock" ? "" : "sales-no-stock")}
          active={quick === "sales-no-stock"}
        />
        <Kpi small label="Без типа продукта" value={int0(attrsQuality.noType)} />
        <Kpi small label="Без характеристики" value={int0(attrsQuality.noChar)} />
      </div>

      {/* Графики + инсайты (по отфильтрованным строкам, как в оригинале) */}
      <div className="grid gap-3 lg:grid-cols-[3fr_2fr]">
        <OrdersBarChart rows={filtered} ctx={ctx} />
        <MpDonut rows={filtered} />
      </div>
      <Insights rows={filtered} ctx={ctx} />

      {/* Тулбар фильтров */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Поиск: артикул, название, бренд, код 1С"
          className="h-9 w-72"
        />
        <select
          value={abc}
          onChange={(e) => setAbc(e.target.value)}
          className="h-9 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">ABC/XYZ: все</option>
          {ABC_VALUES.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
        <select
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          className="h-9 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Бренд: все</option>
          {brands.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <select
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          className="h-9 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Тип продукта: все</option>
          {(typesQ.data ?? []).map((t) => (
            <option key={t.id} value={t.name}>{t.name}</option>
          ))}
        </select>
        <select
          value={characteristic}
          onChange={(e) => setCharacteristic(e.target.value)}
          className="h-9 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Характеристика: все</option>
          {(charsQ.data ?? []).map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as TableMode)}
          className="h-9 rounded-md border bg-background px-2 text-sm"
        >
          {(Object.keys(MODE_LABELS) as TableMode[]).map((m) => (
            <option key={m} value={m}>{MODE_LABELS[m]}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={resetFilters}
          className="h-9 rounded-md border px-3 text-sm text-muted-foreground hover:text-foreground"
        >
          Сброс
        </button>
        <span className="ml-auto text-xs text-muted-foreground">
          строк: {int0(filtered.length)}
        </span>
        <ExportButtons
          rowsCsv={() =>
            filtered.map((r): ArticleCsvRow => {
              const m = rowMetrics(r, ctx);
              const a = attrsMap.get(r.article);
              return {
                article: r.article, name: r.name,
                group: a?.product_type ?? "", characteristic: a?.characteristic ?? "",
                abcxyz: r.abcxyz, qty: r.qtySum, current: m.current, previous: m.previous,
                last30: m.last30, stock_mp: r.stockMp, stock_1c: r.stock1c,
                stock_total: r.stockTotal, last_sale: r.lastSale ?? "-", mps: r.mps,
              };
            })
          }
          excelParams={{
            date_from: report.date_from, date_to: report.date_to,
            tab, search, abc, quick,
          }}
        />
      </div>

      {/* Быстрые срезы */}
      <div className="flex flex-wrap gap-1.5">
        {QUICK_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            onClick={() => setQuick(f.value)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs transition-colors",
              quick === f.value
                ? "border-primary bg-primary/10 font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Сравнение отклонений (по чекбоксам «Сравнить») */}
      <ComparisonPanel rows={filtered} ctx={ctx} compared={compared} />

      <MatrixTable
        rows={filtered}
        ctx={ctx}
        mode={mode}
        sort={sort}
        onSortChange={setSort}
        extras={extras}
        expandedKey={expandedKey}
        onToggleExpand={(key) => setExpandedKey((prev) => (prev === key ? null : key))}
        renderDetail={(row, m) => (
          <ArticleDetail row={row} m={m} ctx={ctx} report={report} tab={tab} />
        )}
      />

      <BulkActionMenu
        count={bulkSelected.size}
        canEdit={canEdit}
        onPaint={(color) => highlightMut.mutate({ articles: [...bulkSelected], color })}
        onClearColor={() => highlightMut.mutate({ articles: [...bulkSelected], color: null })}
        onUnselect={() => setBulkSelected(new Set())}
      />
    </div>
  );
}

function ExportButtons({
  rowsCsv,
  excelParams,
}: {
  rowsCsv: () => ArticleCsvRow[];
  excelParams: Record<string, string>;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const btn = "h-9 rounded-md border px-3 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50";
  return (
    <>
      <button
        type="button"
        className={btn}
        onClick={() => downloadTextFile("orders-dashboard-articles.csv", buildArticlesCsv(rowsCsv()))}
      >
        CSV артикулы
      </button>
      <button
        type="button"
        className={btn}
        onClick={() => downloadTextFile("orders-dashboard-details.csv", buildDetailsCsv(rowsCsv()))}
      >
        CSV детали
      </button>
      <button
        type="button"
        className={btn}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const { data } = await api.get("/orders/export/excel", {
              params: excelParams,
              responseType: "blob",
            });
            saveBlob(data as Blob, "orders-dashboard-analytics.xlsx");
          } catch (e) {
            toast.error(extractErrorMessage(e, "Не удалось выгрузить Excel"));
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Excel…" : "Excel"}
      </button>
    </>
  );
}

function Kpi({
  label,
  value,
  valueClass,
  small,
  onClick,
  active,
}: {
  label: string;
  value: string;
  valueClass?: string;
  small?: boolean;
  onClick?: () => void;
  active?: boolean;
}): React.ReactElement {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "rounded-md border bg-card px-3 py-2 text-left",
        onClick && "cursor-pointer transition-colors hover:bg-accent/50",
        active && "border-primary bg-primary/5",
      )}
    >
      <div className="truncate text-[11px] text-muted-foreground" title={label}>
        {label}
      </div>
      <div className={cn(small ? "text-base" : "text-lg", "font-semibold tabular-nums", valueClass)}>
        {value}
      </div>
    </Comp>
  );
}

export { rowMetrics };
